// /onboarding/shape — shape 6, «طاولاتك ومناطقها» (board 25 v2 shape--6,
// board 24): the tables grouped by zone («داخلي» / «خارجي» / «عائلي»), each
// «{name}», «{n} مقاعد» and its active switch. «إضافة طاولة» asks the zone,
// the name (default «طاولة {n}») and the seats (1..50). The deposit switch
// «العربون» lives on the business's scheduling (table_deposit_*), written by
// the idempotent scheduling PUT: off = «مطفأ · الحجز بلا دفع مسبق»; on =
// «للشخص» | «للطاولة» and the amount (ruling W7). Finish needs one active table.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { Chip, Toggle } from '@/components/Form';
import { Skeleton } from '@/components/Skeleton';
import { normalizeDigits, toArabicDigits } from '@/lib/digits';
import { useLayout } from '@/lib/layout';
import { Msg } from '../../components/widgets';
import { Wizard } from '../../components/Wizard';
import { SHAPE_COMMON, SHAPE_COPY, TABLES } from '../../copy';
import { putScheduling } from '../../data/api';
import { finishShape } from '../../data/enroll';
import { schedulingBody } from '../../data/shapes';
import { createTable, getTableDeposit, listTables, patchTable, type Table, type TableZone } from '../../data/shapeApi';
import { onboardingStore } from '../../state';
import { useBusinessPreview } from '../Business';
import { firstMessage, useShapeAction } from './useShapeAction';

const ZONES: TableZone[] = ['indoor', 'outdoor', 'family'];
type DepositKind = 'per_person' | 'per_table';
type DepositDraft = { enabled: boolean; kind: DepositKind | null; value: string };
const keyOf = (d: DepositDraft) => (d.enabled ? `on:${d.kind}:${Number(d.value) || 0}` : 'off');

export function TablesStep({ businessId }: { businessId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mobile = useLayout() === 'phone';

  const key = ['onboarding', 'tables', businessId];
  const tables = useQuery({ queryKey: key, queryFn: () => listTables(businessId), staleTime: Infinity, retry: 0 });
  const stored = useQuery({ queryKey: ['onboarding', 'table-deposit', businessId], queryFn: () => getTableDeposit(businessId), staleTime: Infinity, retry: 0 });
  const { act, busy, error } = useShapeAction();
  const [zone, setZone] = useState<TableZone>('indoor');
  const [name, setName] = useState<string | null>(null);
  const [seats, setSeats] = useState('4');
  const [addError, setAddError] = useState<string | null>(null);
  const [deposit, setDeposit] = useState<{ enabled: boolean; kind: DepositKind | null; value: string } | null>(null);
  const [depositError, setDepositError] = useState<string | null>(null);

  useEffect(() => {
    if (deposit == null && stored.data) setDeposit({ enabled: stored.data.enabled, kind: stored.data.kind ?? null, value: stored.data.value != null ? String(stored.data.value) : '' });
  }, [stored.data, deposit]);

  const list = tables.data ?? [];
  const { profile, preview } = useBusinessPreview('shape');
  const hasActive = list.some((t) => t.active);
  const defaultName = TABLES.defaultName(toArabicDigits(list.length + 1));
  const loading = tables.isPending;
  const dep = deposit ?? { enabled: false, kind: null, value: '' };

  const setTables = (fn: (xs: Table[]) => Table[]) => qc.setQueryData<Table[]>(key, (xs) => fn(xs ?? []));

  const add = () =>
    act(
      async () => {
        const t = await createTable(businessId, { name: (name ?? defaultName).trim(), seats: Number(normalizeDigits(seats)) || 0, zone });
        setTables((xs) => [...xs, t]);
        setName(null);
        setAddError(null);
      },
      (e) => setAddError(firstMessage(e)),
    );

  const toggle = (t: Table) =>
    act(async () => {
      const next = await patchTable(businessId, t.id, { active: !t.active });
      setTables((xs) => xs.map((x) => (x.id === t.id ? next : x)));
    });

  /** What the server holds for the deposit (the last successful write, or the stored read). */
  const [written, setWritten] = useState<string | null>(null);
  const storedKey = stored.data ? keyOf({ enabled: stored.data.enabled, kind: stored.data.kind ?? null, value: stored.data.value != null ? String(stored.data.value) : '' }) : null;
  const serverKey = written ?? storedKey;
  const complete = (d: DepositDraft) => !d.enabled || (!!d.kind && !!d.value.trim() && Number.isFinite(Number(normalizeDigits(d.value))));

  /** The deposit onto the scheduling (idempotent PUT): off at once; on once the kind and the amount are there. */
  const writeDeposit = async (d: DepositDraft): Promise<boolean> => {
    setDeposit(d);
    setDepositError(null);
    if (!complete(d)) return false;
    const value = Number(normalizeDigits(d.value));
    const s = onboardingStore.get();
    const body = schedulingBody('table', s.type, {
      sittingMin: s.sittingMin,
      tableDeposit: d.enabled ? { enabled: true, kind: d.kind!, value } : { enabled: false },
    });
    return act(
      async () => {
        await putScheduling(businessId, body);
        setWritten(keyOf(d));
      },
      (e) => setDepositError(firstMessage(e)),
    );
  };

  /** Finish never leaves the switch saying something the server does not hold (M3). */
  const finish = async () => {
    if (!hasActive) return;
    if (!complete(dep)) {
      setDepositError('أدخل قيمة العربون');
      return;
    }
    if (keyOf(dep) !== serverKey && !(await writeDeposit(dep))) return;
    finishShape(navigate);
  };

  return (
    <Wizard
      screen="shape"
      testId={loading ? 'onb-shape-loading' : 'onb-shape-tables'}
      state={loading ? 'loading' : busy ? 'saving' : 'default'}
      list
      question={SHAPE_COPY.table.question()}
      sub={SHAPE_COPY.table.sub()}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} disabled={!hasActive}>
          {SHAPE_COMMON.finish}
        </PrimaryAction>
      }
      onSubmit={() => void finish()}
      error={tables.isError ? { kind: 'network', onRetry: () => void tables.refetch() } : error}
      panel={{ phase: 'preview', profile, preview }}
    >
      {loading ? (
        <Skeleton height={120} />
      ) : (
        <>
          {ZONES.filter((z) => list.some((t) => t.zone === z)).map((z) => (
            <div key={z} className="onb-entity-section" data-testid={`onb-table-zone-${z}`}>
              <div className="onb-entity-section-title">{TABLES.zones[z]}</div>
              <div className="onb-listcard">
                {list
                  .filter((t) => t.zone === z)
                  .map((t) => (
                    <div key={t.id} className="onb-row" data-testid="onb-table-row" data-active={t.active ? 'true' : 'false'}>
                      <div className="onb-row-text">
                        <div className="onb-row-title">{t.name}</div>
                        <div className="onb-row-sub">{TABLES.seats(toArabicDigits(t.seats))}</div>
                      </div>
                      <button type="button" role="switch" aria-checked={t.active} aria-label={t.name} className="onb-day-toggle" onClick={() => void toggle(t)}>
                        <span className="onb-day-knob" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          ))}

          <div className="onb-listcard onb-table-add" data-testid="onb-table-add">
            <div className="onb-row onb-band-row">
              <div className="onb-sitting-opts" role="group" aria-label={TABLES.zone}>
                {ZONES.map((z) => (
                  <Chip key={z} selected={zone === z} onClick={() => setZone(z)}>
                    {TABLES.zones[z]}
                  </Chip>
                ))}
              </div>
              <input className="onb-entity-name" aria-label={TABLES.name} data-testid="onb-table-name" maxLength={30} value={name ?? defaultName} onChange={(e) => setName(e.target.value)} />
              <input
                className="onb-entity-price ltr"
                dir="ltr"
                inputMode="numeric"
                aria-label={TABLES.seatsLabel}
                data-testid="onb-table-seats"
                value={seats}
                onChange={(e) => setSeats(normalizeDigits(e.target.value))}
              />
            </div>
            <button type="button" className="onb-entity-add" onClick={() => void add()} disabled={busy}>
              <Plus aria-hidden="true" />
              {TABLES.add}
            </button>
          </div>
          {addError && <Msg testId="onb-table-error">{addError}</Msg>}
          {!hasActive && (
            <Msg tone="info" testId="onb-shape-min-one">
              {SHAPE_COMMON.atLeastOne}
            </Msg>
          )}

          <div className="onb-listcard onb-branches" data-testid="onb-table-deposit">
            <Toggle checked={dep.enabled} onChange={(enabled) => void writeDeposit({ ...dep, enabled })} label={TABLES.deposit} description={dep.enabled ? undefined : TABLES.depositOff} />
            {dep.enabled && (
              <div className="onb-row onb-band-row">
                <div className="onb-sitting-opts">
                  <Chip selected={dep.kind === 'per_person'} onClick={() => void writeDeposit({ ...dep, kind: 'per_person' })}>
                    {TABLES.perPerson}
                  </Chip>
                  <Chip selected={dep.kind === 'per_table'} onClick={() => void writeDeposit({ ...dep, kind: 'per_table' })}>
                    {TABLES.perTable}
                  </Chip>
                </div>
                <input
                  className="onb-entity-price ltr"
                  dir="ltr"
                  inputMode="numeric"
                  aria-label={TABLES.amount}
                  data-testid="onb-table-deposit-value"
                  value={dep.value}
                  onChange={(e) => setDeposit({ ...dep, value: normalizeDigits(e.target.value) })}
                  onBlur={(e) => void writeDeposit({ ...dep, value: normalizeDigits(e.target.value) })}
                />
              </div>
            )}
          </div>
          {depositError && <Msg testId="onb-table-deposit-error">{depositError}</Msg>}
        </>
      )}
    </Wizard>
  );
}
